import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";

const apiBase = config.api.API_URL;

/** Mensagem leiga do envelope (interceptor rejeita com { message }). */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (
    e &&
    typeof e === "object" &&
    "message" in e &&
    typeof (e as { message: unknown }).message === "string" &&
    (e as { message: string }).message.trim() !== ""
  ) {
    return (e as { message: string }).message;
  }
  return fallback;
}

export type WelcomeAutomation = {
  type: string;
  enabled: boolean;
  campaign_id: number | null;
  campaign?: { id: number; name: string; subject: string } | null;
  delay_minutes: number;
  updated_at?: string | null;
};

export type WinBackAutomationMetrics = {
  sent_count: number;
  skipped_count: number;
  pending_count: number;
  failed_count: number;
  opened_count: number;
  last_enrolled_at: string | null;
};

export type WinBackAutomation = {
  type: string;
  enabled: boolean;
  campaign_id: number | null;
  campaign?: { id: number; name: string; subject: string } | null;
  max_touches: number;
  cooldown_days: number;
  metrics?: WinBackAutomationMetrics;
  updated_at?: string | null;
};

export type AbandonedCartSparkPoint = {
  date: string;
  events: number;
  converted: number;
};

export type AbandonedCartMetrics = {
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
  events_today: number;
  events_total: number;
  converted?: number;
  conversion_bps?: number;
  sent_last_24h?: number;
  last_sent_at?: string | null;
  cancelled_last_24h?: number;
  drip_paused_complaint?: boolean;
  conversion_with_drip_bps?: number;
  conversion_without_drip_bps?: number;
  events_with_drip?: number;
  events_without_drip?: number;
  sparkline_7d?: AbandonedCartSparkPoint[];
};

export type AbandonedCartEventRow = {
  id: number;
  email: string;
  status: string;
  skip_reason: string | null;
  cart_ref: string | null;
  created_at: string | null;
  contact_id: number | null;
  converted_at?: string | null;
  converted_order_id?: string | null;
  drip_ab_bucket?: string | null;
};

export type AbandonedCartCancelledRow = {
  id: number;
  email: string;
  skip_reason: string | null;
  cancelled_at: string | null;
};

export type AbandonedCartAutomation = {
  type: string;
  enabled: boolean;
  campaign_id: number | null;
  campaign?: { id: number; name: string; subject: string } | null;
  delay_minutes: number;
  cooldown_hours: number;
  drip_delay_minutes?: number;
  drip_campaign_id?: number | null;
  drip_ab_enabled?: boolean;
  metrics?: AbandonedCartMetrics;
  recent_events?: AbandonedCartEventRow[];
  recent_cancelled?: AbandonedCartCancelledRow[];
  warnings?: string[];
  updated_at?: string | null;
};

export type AbandonedCartExportFilters = {
  converted?: boolean;
  cancelled?: boolean;
  today?: boolean;
  days?: number;
};

/**
 * Interceptor devolve o envelope { success, data } (não AxiosResponse) — DOC 09 § 7.2.
 * Tipar Promise<ApiSuccess<T>>; na página usar res.data (domínio). Proibido res.data.data.
 */
export const getWelcomeAutomation = (): Promise<
  ApiSuccess<WelcomeAutomation>
> => {
  return axios.get(apiBase + "/automations/welcome");
};

export const updateWelcomeAutomation = (payload: {
  enabled?: boolean;
  campaign_id?: number | null;
  delay_minutes?: number;
}): Promise<ApiSuccess<WelcomeAutomation>> => {
  return axios.put(apiBase + "/automations/welcome", payload);
};

export const getWinBackAutomation = (): Promise<ApiSuccess<WinBackAutomation>> => {
  return axios.get(apiBase + "/automations/win-back");
};

export const updateWinBackAutomation = (payload: {
  enabled?: boolean;
  campaign_id?: number | null;
  max_touches?: number;
  cooldown_days?: number;
}): Promise<ApiSuccess<WinBackAutomation>> => {
  return axios.put(apiBase + "/automations/win-back", payload);
};

export const enrollWinBackAutomation = (payload?: {
  days?: number;
  contact_ids?: number[];
}): Promise<
  ApiSuccess<{ enrolled: number; skipped: number; days: number }>
> => {
  return axios.post(apiBase + "/automations/win-back/enroll", payload || {});
};

export const getAbandonedCartAutomation = (opts?: {
  today?: boolean;
}): Promise<ApiSuccess<AbandonedCartAutomation>> => {
  const qs = opts?.today ? "?today=1" : "";
  return axios.get(apiBase + "/automations/abandoned-cart" + qs);
};

export const updateAbandonedCartAutomation = (payload: {
  enabled?: boolean;
  campaign_id?: number | null;
  delay_minutes?: number;
  cooldown_hours?: number;
  drip_delay_minutes?: number;
  drip_campaign_id?: number | null;
  drip_ab_enabled?: boolean;
}): Promise<ApiSuccess<AbandonedCartAutomation>> => {
  return axios.put(apiBase + "/automations/abandoned-cart", payload);
};

export const testAbandonedCartAutomation = (email: string): Promise<
  ApiSuccess<{
    queued: boolean;
    event_id: number;
    contact_id: number;
    warnings?: string[];
  }>
> => {
  return axios.post(apiBase + "/automations/abandoned-cart/test", { email });
};

/** CSV direto (não envelope) — dispara download no browser. */
export const downloadAbandonedCartCsv = async (
  filters: AbandonedCartExportFilters = {}
): Promise<void> => {
  const params = new URLSearchParams();
  if (filters.converted) params.set("converted", "1");
  if (filters.cancelled) params.set("cancelled", "1");
  if (filters.today) params.set("today", "1");
  if (filters.days) params.set("days", String(filters.days));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const data = await axios.get(
    apiBase + "/automations/abandoned-cart/export" + qs,
    { responseType: "blob" }
  );
  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "text/csv;charset=utf-8",
        });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `carrinho_abandonado_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
